import { mergeBandPins } from '@tunasong/models'
import { isBandSpace, isPoll } from '@tunasong/schemas'
import type { BandSpace, Entity, Persisted, Poll } from '@tunasong/schemas'
import { mergePollPatch } from './merge-poll.js'

/**
 * The one switch for conditional entity saves (GH #1912).
 *
 * On, a save that opts in (`updateEntity` with `conditional: true` — what `useEntityUpdate` sends) carries If-Match
 * against the version it was based on, AND handles the 412 itself. The two halves go on together or not at all: the
 * client drops a failed save, so If-Match without the 412 handling would discard edits that last-write-wins kept.
 *
 * Off, every save is the unconditional write it was before.
 */
export const conditionalEntitySaves = true

/**
 * Fields the server derives per read rather than stores: a fresh signature on every GET, so they always differ and
 * say nothing about another writer.
 */
const perReadFields = new Set(['storageUrls'])

/** Both values come from the same server serializer, so equal content serializes equally */
const sameValue = (a: unknown, b: unknown) => a === b || JSON.stringify(a) === JSON.stringify(b)

/**
 * The fields of `patch` that another writer changed between `base` (the version the save was based on) and
 * `current` (the server's version now). Empty means the save can be resent on `current` without overwriting
 * anything anyone else wrote.
 */
export const fieldsChangedElsewhere = (patch: object, base: object, current: object): string[] =>
  Object.keys(patch).filter(
    key =>
      !perReadFields.has(key) &&
      !sameValue((base as Record<string, unknown>)[key], (current as Record<string, unknown>)[key])
  )

/**
 * The switch for the conflict notice (GH #1912 option B, docs/ux/2026-10-24-conflict-notice-ux.md).
 *
 * On, a structured field that changed on both sides and could not be rebased is held as unsaved and recorded in
 * `entities.conflicts` for the notice to resolve. Off, it is last-write-wins as before. On only with the notice UI
 * mounted (the panel banners and the app-wide `ConflictSnackbarHost`): a held edit with no banner to resolve it would
 * sit unsaved until the page is hidden.
 */
export const conflictNotice = true

/** Arrays and objects: the values whose overwrite hides what it loses (spec, Scope). Scalars stay last-write-wins. */
const isStructuredValue = (value: unknown) => typeof value === 'object' && value !== null

/** The fields of `patch` another writer changed since `base` whose value, mine or theirs, is structured */
export const conflictedStructuredFields = (patch: object, base: object, current: object): string[] =>
  fieldsChangedElsewhere(patch, base, current).filter(
    key =>
      isStructuredValue((patch as Record<string, unknown>)[key]) ||
      isStructuredValue((current as Record<string, unknown>)[key])
  )

/** The entries of `source` named in `keys` */
export const pickFields = <T extends object>(source: T, keys: readonly string[]): Partial<T> =>
  Object.fromEntries(Object.entries(source).filter(([key]) => keys.includes(key))) as Partial<T>

/** The entries of `source` not named in `keys` */
export const omitFields = <T extends object>(source: T, keys: readonly string[]): Partial<T> =>
  Object.fromEntries(Object.entries(source).filter(([key]) => !keys.includes(key))) as Partial<T>

/**
 * Whether the page is going away or out of sight: `visibilityState` is already `hidden` during `pagehide` and unload.
 *
 * A save then must be ONE keepalive request, as it was before conditional saves (GH #2476): the JavaScript between
 * a rebase's GET and its PATCH does not run once the page is gone, so a rebase there would lose the edit.
 */
export const isPageHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden'

/** How many times a save refetches and rebases after a 412 before it falls back to last-write-wins */
export const maxRebases = 2

/**
 * The patch to resend on `current`, the server's version now, given `base`, the version the patch was made from:
 *   - the patch itself when nobody else changed its fields;
 *   - for a poll, the index-preserving merge of its options and groups, since responses and `finalOption` point at
 *     options by index and an overwrite can move votes (see `merge-poll.ts`);
 *   - for a band space, my pins and unpins re-applied to the pins made since (`mergeBandPins`);
 *   - `undefined` otherwise — the same field changed on both sides, and the caller falls back.
 */
export const rebasedPatch = (
  patch: Partial<Entity>,
  base: Persisted<Entity>,
  current: Persisted<Entity>
): Partial<Entity> | undefined => {
  if (fieldsChangedElsewhere(patch, base, current).length === 0) {
    return patch
  }
  if (isPoll(base) && isPoll(current)) {
    return mergePollPatch({ base, theirs: current, patch: patch as Partial<Poll> }) as Partial<Entity> | undefined
  }
  /** GH #2822: a band pin merges with pins made since, as long as the pins are all another writer changed */
  const bandPatch = patch as Partial<BandSpace>
  if (isBandSpace(base) && isBandSpace(current) && bandPatch.pins) {
    const changed = fieldsChangedElsewhere(patch, base, current)
    if (changed.every(field => field === 'pins')) {
      return {
        ...bandPatch,
        pins: mergeBandPins({ base: base.pins, theirs: current.pins, mine: bandPatch.pins }),
      } as Partial<Entity>
    }
  }
  return undefined
}
